/**
 * PCC Q&A — Wave 3 (hand-written, one unique answer per entry).
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-06)
 * - ผู้ออก PCC ของไทย: กองทะเบียนประวัติอาชญากร สำนักงานตำรวจแห่งชาติ
 *   (ศูนย์บริการออกหนังสือรับรองความประพฤติ) — royalthaipolice.go.th
 * - นิติกรณ์คำแปล/เอกสาร: กองสัญชาติและนิติกรณ์ กรมการกงสุล
 *   ค่าธรรมเนียม 200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน)
 *   — consular.mfa.go.th
 * - ไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผล 28 ก.พ. 2027
 * - ห้ามระบุค่าบริการของเรา · ไม่ยืนยันผลลัพธ์ · เวลาเป็นช่วงโดยประมาณ
 */
import type { PccQA } from "./questions-500";

const NOTE =
  "ข้อมูล ณ ส.ค. 2026 — ระเบียบของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรูปแบบเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

export const PCC_QA_WAVE3: PccQA[] = [
  {
    slug: "pcc-เปลี่ยนชื่อสกุลแล้วขอได้ไหม",
    q: "เคยเปลี่ยนชื่อหรือนามสกุล จะขอ PCC อย่างไรให้ไม่มีปัญหา?",
    aShort:
      "ต้องแจ้งชื่อเดิมทุกชื่อพร้อมแนบหลักฐานการเปลี่ยนชื่อ-สกุล เช่น ใบสำคัญการเปลี่ยนชื่อ ใบสำคัญการสมรสหรือหย่า เพื่อให้การตรวจสอบฐานข้อมูลครอบคลุมทุกชื่อที่เคยใช้ และให้ชื่อบนเอกสารตรงกับหนังสือเดินทาง",
    aLong:
      "หน่วยงานปลายทางมักเทียบชื่อบน PCC กับหนังสือเดินทางและเอกสารวีซ่าตัวอักษรต่อตัวอักษร ถ้าชื่อไม่ตรงจะถูกขอเอกสารเพิ่มหรือถูกตีกลับ วิธีลดความเสี่ยงคือยื่นหลักฐานการเปลี่ยนชื่อพร้อมคำแปลรับรองไปด้วยตั้งแต่ต้น และตรวจการสะกดอักษรโรมันให้ตรงกับหนังสือเดินทางก่อนออกเอกสารจริง " +
      NOTE,
    synonyms: [
      "เปลี่ยนชื่อ ขอใบรับรองความประพฤติ",
      "ชื่อเดิม pcc",
      "name change police clearance thailand",
      "นามสกุลหลังสมรส pcc",
    ],
    cluster: "documents",
  },
  {
    slug: "pcc-สะกดชื่อไม่ตรงพาสปอร์ต",
    q: "ถ้าชื่อบน PCC สะกดไม่ตรงกับหนังสือเดินทางต้องทำอย่างไร?",
    aShort:
      "ต้องขอแก้ไขกับหน่วยงานผู้ออกเอกสารโดยตรง ไม่ควรแก้ไขเองหรือให้ผู้แปลสะกดตามหนังสือเดินทางโดยพลการ เพราะคำแปลต้องตรงกับต้นฉบับ มิฉะนั้นนิติกรณ์และสถานทูตจะไม่รับ",
    aLong:
      "ลำดับที่ถูกต้องคือแก้ที่ต้นฉบับก่อน แล้วจึงแปลและทำนิติกรณ์ตามต้นฉบับที่แก้แล้ว หากแก้ไม่ได้ในทางปฏิบัติ บางปลายทางยอมรับหนังสือชี้แจงพร้อมหลักฐานยืนยันว่าเป็นบุคคลเดียวกัน แต่เป็นดุลพินิจของผู้รับเอกสาร ควรสอบถามล่วงหน้าก่อนเดินเรื่องแปล " +
      NOTE,
    synonyms: [
      "ชื่อผิด pcc แก้ยังไง",
      "สะกดชื่อไม่ตรง พาสปอร์ต เอกสารราชการ",
      "name mismatch police certificate",
    ],
    cluster: "documents",
  },
  {
    slug: "pcc-อายุเอกสารกี่เดือน",
    q: "PCC มีอายุใช้งานกี่เดือน?",
    aShort:
      "ตัวเอกสารไม่ได้กำหนดวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางส่วนใหญ่กำหนดให้ออกไม่เกิน 3 หรือ 6 เดือนนับถึงวันยื่น จึงควรวางลำดับงานให้ PCC เป็นเอกสารชิ้นท้าย ๆ ก่อนยื่นจริง",
    aLong:
      "เมื่อรวมเวลารอผลตรวจสอบ เวลาแปล เวลานิติกรณ์ และคิวสถานทูต อายุเอกสารอาจถูกใช้ไปหลายสัปดาห์โดยไม่รู้ตัว แนวปฏิบัติที่ปลอดภัยคือยืนยันเกณฑ์อายุเอกสารจากเช็กลิสต์ปลายทางก่อน แล้วจึงนับถอยหลังกำหนดวันขอ PCC ให้เหลือระยะกันเผื่อไว้อย่างน้อยสองถึงสี่สัปดาห์ " +
      NOTE,
    synonyms: [
      "ใบรับรองความประพฤติ อายุ",
      "pcc หมดอายุ",
      "police clearance validity thailand",
    ],
    cluster: "process",
  },
  {
    slug: "pcc-ยื่นจากต่างประเทศทำอย่างไร",
    q: "อยู่ต่างประเทศ ขอ PCC ของไทยได้ไหม?",
    aShort:
      "ทำได้โดยยื่นผ่านสถานเอกอัครราชทูตหรือสถานกงสุลไทยในประเทศที่พำนัก หรือมอบอำนาจให้ผู้แทนในไทยดำเนินการ พร้อมแผ่นลายพิมพ์นิ้วมือที่จัดทำโดยหน่วยงานที่ได้รับการยอมรับ",
    aLong:
      "จุดที่ทำให้เรื่องล่าช้าที่สุดคือแผ่นลายพิมพ์นิ้วมือไม่ชัดหรือกรอกข้อมูลไม่ครบ และหนังสือมอบอำนาจไม่ได้ระบุขอบเขตให้ครอบคลุมการรับเอกสารและการยื่นนิติกรณ์ต่อ ควรเตรียมสำเนาหนังสือเดินทางที่ลงนามรับรองสำเนา หนังสือมอบอำนาจที่ลงนามต่อหน้าเจ้าหน้าที่สถานทูตไทย และที่อยู่จัดส่งที่ตรวจสอบได้ " +
      NOTE,
    synonyms: [
      "ขอใบรับรองความประพฤติ จากต่างประเทศ",
      "pcc ทางไปรษณีย์",
      "apply thai police clearance from abroad",
      "มอบอำนาจขอ pcc",
    ],
    cluster: "overseas",
  },
  {
    slug: "pcc-ชาวต่างชาติเคยอยู่ไทยขอได้ไหม",
    q: "ชาวต่างชาติที่เคยพำนักในไทยขอ PCC ของไทยได้หรือไม่?",
    aShort:
      "ขอได้ หากเคยพำนักในไทยตามเงื่อนไขที่หน่วยงานกำหนด โดยต้องแสดงหนังสือเดินทาง หลักฐานการพำนัก เช่น วีซ่าหรือใบอนุญาตทำงาน และลายพิมพ์นิ้วมือตามแบบที่กำหนด",
    aLong:
      "หน่วยงานปลายทางในต่างประเทศมักขอ PCC จากทุกประเทศที่ผู้ยื่นเคยพำนักเกินระยะเวลาที่กำหนด เช่น 6 หรือ 12 เดือน ผู้ที่เคยทำงานในไทยจึงต้องขอฉบับของไทยเพิ่ม ควรเตรียมสำเนาหน้าวีซ่าและตราประทับเข้า-ออกเมืองย้อนหลังให้ครบ เพราะเป็นหลักฐานยืนยันช่วงเวลาพำนัก " +
      NOTE,
    synonyms: [
      "ต่างชาติขอ pcc ไทย",
      "expat thai police clearance",
      "เคยทำงานในไทย ขอใบรับรองความประพฤติ",
    ],
    cluster: "foreigner",
  },
  {
    slug: "pcc-เคยมีคดีแต่ยกฟ้อง",
    q: "เคยถูกดำเนินคดีแต่ศาลยกฟ้อง จะแสดงในใบ PCC ไหม?",
    aShort:
      "ผลที่ปรากฏขึ้นกับข้อมูลที่บันทึกอยู่ในฐานข้อมูลของหน่วยงานผู้ออก ไม่สามารถระบุล่วงหน้าแทนหน่วยงานได้ หากข้อมูลไม่ตรงกับคำพิพากษา เจ้าตัวต้องยื่นคำร้องขอตรวจสอบและแก้ไขกับหน่วยงานโดยตรง",
    aLong:
      "ผู้ให้บริการเอกสารไม่มีอำนาจแก้ไขหรือลบข้อมูลในฐานทะเบียนประวัติ สิ่งที่ทำได้คือช่วยจัดเตรียมสำเนาคำพิพากษาหรือหนังสือรับรองคดีถึงที่สุดพร้อมคำแปลรับรอง เพื่อยื่นประกอบคำร้องกับหน่วยงานผู้ออก และเพื่อใช้ชี้แจงต่อหน่วยงานปลายทางเมื่อจำเป็น " +
      NOTE,
    synonyms: [
      "ยกฟ้อง ประวัติอาชญากร",
      "ลบประวัติอาชญากรรม ไทย",
      "criminal record expungement thailand",
    ],
    cluster: "legal",
  },
  {
    slug: "pcc-ใช้สมัครงานต่างประเทศ",
    q: "นายจ้างต่างประเทศขอ PCC ต้องเตรียมรูปแบบไหน?",
    aShort:
      "ส่วนใหญ่ขอฉบับภาษาอังกฤษที่ออกโดยหน่วยงานตำรวจระดับชาติ พร้อมนิติกรณ์กรมการกงสุล และบางประเทศขอตราประทับสถานทูตปลายทางเพิ่ม ควรขอเช็กลิสต์เป็นลายลักษณ์อักษรจากนายจ้างก่อน",
    aLong:
      "คำที่นายจ้างใช้ต่างกัน เช่น police certificate, criminal record check, certificate of good conduct แต่สิ่งที่ต้องยืนยันมีสามข้อคือ ผู้ออกต้องเป็นหน่วยงานตำรวจระดับชาติหรือไม่ ต้องเป็นแบบลายพิมพ์นิ้วมือหรือไม่ และต้องผ่านการรับรองชั้นใดบ้าง เมื่อได้คำตอบครบสามข้อจึงวางลำดับงานได้โดยไม่ต้องทำซ้ำ " +
      NOTE,
    synonyms: [
      "สมัครงานต่างประเทศ ใบรับรองความประพฤติ",
      "employer police check thailand",
      "pcc สำหรับนายจ้าง",
    ],
    cluster: "employment",
  },
  {
    slug: "pcc-ต้องผ่านกงสุลไหม",
    q: "PCC ต้องผ่านนิติกรณ์กรมการกงสุลทุกครั้งหรือไม่?",
    aShort:
      "ไม่เสมอไป ขึ้นกับข้อกำหนดของหน่วยงานปลายทาง หากปลายทางระบุว่าเอกสารต้อง legalised หรือต้องมีตราประทับสถานทูต จึงต้องผ่านกรมการกงสุลก่อนเป็นลำดับแรก",
    aLong:
      "ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลคือ 200 บาทต่อตราประทับสำหรับบริการปกติ และ 400 บาทต่อตราประทับสำหรับบริการด่วน ตามอัตราที่ประกาศไว้ที่ consular.mfa.go.th เมื่อผ่านกรมการกงสุลแล้วจึงนำไปยื่นสถานทูตปลายทางตามที่ปลายทางกำหนด ลำดับสลับกันไม่ได้ " +
      NOTE,
    synonyms: [
      "นิติกรณ์ ใบรับรองความประพฤติ",
      "legalise police clearance thailand",
      "กงสุล รับรอง pcc",
    ],
    cluster: "embassy",
  },
  {
    slug: "pcc-apostille-2027-เปลี่ยนอะไร",
    q: "หลังไทยเข้าอนุสัญญา Apostille ปี 2027 ขั้นตอน PCC จะเปลี่ยนไปอย่างไร?",
    aShort:
      "อนุสัญญามีผลกับไทยวันที่ 28 กุมภาพันธ์ 2027 หลังจากนั้นเอกสารที่ส่งไปประเทศภาคีจะใช้ตรา Apostille แทนการประทับตราสถานทูตปลายทาง แต่ก่อนวันดังกล่าวยังต้องใช้เส้นทางเดิม",
    aLong:
      "สำหรับงานที่ต้องยื่นภายในปี 2026 ควรวางแผนตามเส้นทางปัจจุบันคือ แปล → นิติกรณ์กรมการกงสุล → สถานทูตปลายทาง ส่วนงานที่กำหนดยื่นหลังวันที่อนุสัญญามีผล ควรรอยืนยันแนวปฏิบัติที่หน่วยงานประกาศอีกครั้ง เพราะรูปแบบตราและช่องทางยื่นอาจกำหนดเพิ่มเติมภายหลัง " +
      NOTE,
    synonyms: [
      "apostille ไทย 2027 pcc",
      "hague apostille thailand police certificate",
      "เลิกใช้ตราสถานทูต",
    ],
    cluster: "apostille",
  },
  {
    slug: "pcc-สาเหตุถูกตีกลับ",
    q: "สาเหตุที่ PCC ถูกตีกลับบ่อยที่สุดมีอะไรบ้าง?",
    aShort:
      "ห้าสาเหตุที่พบบ่อยคือ เอกสารเกินอายุที่ปลายทางกำหนด ชื่อสะกดไม่ตรงหนังสือเดินทาง ขอผิดประเภทระหว่างแบบชื่อกับแบบลายพิมพ์นิ้วมือ ขาดชั้นการรับรองที่ปลายทางกำหนด และคำแปลไม่ตรงต้นฉบับ",
    aLong:
      "วิธีป้องกันคือทำเช็กลิสต์สี่บรรทัดก่อนเริ่มงานทุกครั้ง ได้แก่ ประเภทเอกสารที่ปลายทางระบุ ชั้นการรับรองที่ต้องมี อายุเอกสารสูงสุด และรูปแบบคำแปลที่ยอมรับ จากนั้นถ่ายภาพเอกสารทุกขั้นเก็บไว้เป็นหลักฐาน หากถูกสอบถามภายหลังจะชี้แจงได้เร็วโดยไม่ต้องเริ่มใหม่ " +
      NOTE,
    synonyms: [
      "pcc ถูกปฏิเสธ",
      "ใบรับรองความประพฤติ ไม่ผ่าน",
      "police clearance rejected reasons",
    ],
    cluster: "process",
  },
  {
    slug: "pcc-ขอหลายฉบับพร้อมกัน",
    q: "ขอ PCC หลายฉบับพร้อมกันเพื่อใช้หลายประเทศได้ไหม?",
    aShort:
      "โดยทั่วไปขอได้หลายฉบับในคราวเดียว ซึ่งช่วยประหยัดเวลารอคิว แต่ละฉบับยังต้องผ่านนิติกรณ์และตราสถานทูตแยกตามปลายทาง และค่าธรรมเนียมราชการคิดตามจำนวนตราประทับ",
    aLong:
      "การขอหลายฉบับเหมาะกับผู้ที่ยื่นวีซ่าหรือสมัครงานหลายประเทศในช่วงเวลาใกล้กัน เพราะรอบการตรวจสอบฐานข้อมูลเป็นขั้นที่ใช้เวลามากที่สุด อย่างไรก็ตามอายุเอกสารเริ่มนับตั้งแต่วันที่ออก จึงไม่ควรขอเผื่อไว้นานเกินไปจนเลยเกณฑ์ของปลายทางที่ยื่นทีหลัง " +
      NOTE,
    synonyms: [
      "ขอ pcc หลายใบ",
      "multiple police clearance copies",
      "ใบรับรองความประพฤติ หลายฉบับ",
    ],
    cluster: "process",
  },
  {
    slug: "pcc-ต้องมาด้วยตนเองไหม",
    q: "ต้องไปพิมพ์ลายนิ้วมือด้วยตนเองหรือมอบอำนาจได้?",
    aShort:
      "ขั้นตอนพิมพ์ลายนิ้วมือของแบบ Type 2 ต้องทำโดยตัวผู้ยื่นเองที่หน่วยงานที่รับพิมพ์ลายนิ้วมือ ส่วนขั้นยื่นคำร้องและรับเอกสารสามารถมอบอำนาจให้ผู้แทนดำเนินการได้",
    aLong:
      "ผู้ที่อยู่ต่างประเทศสามารถพิมพ์ลายนิ้วมือกับหน่วยงานตำรวจท้องถิ่นหรือที่สถานทูตไทยตามที่แต่ละแห่งให้บริการ แล้วส่งแผ่นลายพิมพ์กลับมาให้ผู้รับมอบอำนาจยื่นต่อ ควรตรวจว่าแผ่นลายพิมพ์มีลายมือชื่อเจ้าหน้าที่ผู้จัดทำและตราประทับครบถ้วน มิฉะนั้นจะถูกขอให้พิมพ์ใหม่ " +
      NOTE,
    synonyms: [
      "พิมพ์ลายนิ้วมือ pcc",
      "fingerprint form police clearance thailand",
      "มอบอำนาจรับใบรับรองความประพฤติ",
    ],
    cluster: "process",
  },
];
